import React from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { salesProposalsEndpoint } from '../../../api/endpoints';
import { DataTable, DataTableColumn } from '../../../components/DataTable/DataTable';
import { RequestedByCell } from '../../../components/RequestedByCell';
import { ProposalFlowModal } from '../../../components/ProposalFlow/ProposalFlowModal';
import { useCommonParams } from '../../../hooks/useCommonParams';
import { useResourceList } from '../../../hooks/useResourceList';
import { formatCurrency } from '../../../utils/formatCurrency';
import { initialsFromName, isRmnEntity } from '../../../utils';
import { CONTENT_TYPE_CONFIG } from '../../../config/contentTypeConfig';
import { Proposal } from './types';

// Proposals list, rendered as a tab of the Sales page (Leads | PDP Audit
// Reports | Proposals). Search/pagination state is owned by SalesAdmin and
// shared across tabs; the "New Proposal" header action opens the wizard
// modal via modalOpen/onCloseModal.
const PAGE_SIZE = 25;

const formatDate = (iso: string) => (iso ? dayjs(iso).format("MMM DD, YYYY h:mm A") : "—");

const contentTypeLabel = (name?: string | null) => {
    if (!name) return "—";
    const cfg = CONTENT_TYPE_CONFIG[name.trim()];
    return cfg ? cfg.title : name;
};

interface ProposalsTabProps {
    searchText: string;
    searchParams: ReturnType<typeof useCommonParams>["searchParams"];
    setSearchParams: ReturnType<typeof useCommonParams>["setSearchParams"];
    page: number;
    setPage: (n: number) => void;
    onCountChange: (n: number) => void;
    extraParams?: Record<string, string | number | undefined>;
    modalOpen: boolean;
    onCloseModal: () => void;
}

export function ProposalsTab({ modalOpen, onCloseModal, ...props }: ProposalsTabProps) {
    const navigate = useNavigate();

    const list = useResourceList<Proposal>({
        endpoint: salesProposalsEndpoint,
        pageSize: PAGE_SIZE,
        errorLabel: "Failed to load strategies",
        ...props,
    });

    const columns: DataTableColumn<Proposal>[] = [
        {
            key: "contact",
            header: "Brand / Contact",
            render: (p) => {
                const brandName = p.brand?.name || p.proposal_meta?.brand_name || null;
                return (
                    <div className="flex items-center gap-[10px]">
                        <div className="w-[30px] h-[30px] rounded-[7px] bg-tint text-accent-text text-[11px] font-bold flex items-center justify-center shrink-0 overflow-hidden">
                            {p.brand?.logo ? (
                                <img src={p.brand.logo} alt="" className="w-full h-full object-cover" />
                            ) : (
                                initialsFromName(brandName || p.lead?.email)
                            )}
                        </div>
                        <div className="min-w-0">
                            <div className="font-semibold text-text-1 truncate">
                                {brandName || "—"}
                                {!p.brand && p.proposal_meta?.brand_name ? (
                                    <span className="ml-1 text-[10.5px] font-semibold text-accent-text">unverified</span>
                                ) : null}
                            </div>
                            {p.lead?.email && (
                                <div className="text-[11.5px] text-text-2 truncate">{p.lead.email}</div>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            key: "requested_by",
            header: "Requested by",
            render: (p) => <RequestedByCell user={p.creator} lead={p.lead} />,
        },
        {
            key: "product",
            header: "Product",
            render: (p) => {
                const title =
                    p.proposal_meta?.product_title ||
                    (p.name.split(" - ").length > 2 ? p.name.split(" - ").slice(2).join(" - ") : null);
                return (
                    <div className="text-text-2">
                        <div>{title || "—"}</div>
                        {p.pdp_audit_report && (
                            <span
                                title={`From PDP audit of ${p.pdp_audit_report.url}`}
                                className="inline-block mt-0.5 text-[10.5px] bg-[#EEF3FA] text-[#3B6EA0] px-2 py-[1px] rounded-full font-semibold"
                            >
                                🔍 From PDP audit
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            key: "objective",
            header: "Objective",
            render: (p) => (
                <div className="text-text-2">
                    <div>{p.objective?.name || "—"}</div>
                    {p.secondary_objective && (
                        <div className="text-[11px] text-text-2">+ {p.secondary_objective.name}</div>
                    )}
                </div>
            ),
        },
        {
            key: "tier",
            header: "Tier",
            render: (p) =>
                p.tier ? (
                    <span className="text-[11px] bg-surface-2 text-text-2 px-2 py-[2px] rounded-md font-semibold capitalize">
                        {p.tier}
                    </span>
                ) : (
                    <span className="text-muted">—</span>
                ),
        },
        {
            key: "category",
            header: "Category",
            render: (p) => <span className="text-text-2">{p.product_category?.name || "—"}</span>,
        },
        {
            key: "package",
            header: "Package",
            render: (p) => (
                <div className="flex items-center gap-[5px] flex-wrap">
                    {p.projects.slice(0, 4).map((pr) => (
                        <span
                            key={pr.id}
                            className="text-[11px] bg-tint text-accent-text px-2 py-[2px] rounded-md font-medium whitespace-nowrap"
                            title={pr.title}
                        >
                            {contentTypeLabel(pr.content_type?.name)}
                        </span>
                    ))}
                    {p.projects.length > 4 && (
                        <span className="text-[11px] text-text-2 font-semibold">+{p.projects.length - 4}</span>
                    )}
                </div>
            ),
        },
        {
            key: 'estimate',
            header: 'Estimate',
            // RMN deals (rmn:true brand, or a supplier stamped under a
            // network) show no pricing — internal staff included.
            render: (p) => (
                <span className="font-semibold text-text-1">
                    {p.pricing_total != null && !isRmnEntity(p) ? formatCurrency(p.pricing_total) : '—'}
                </span>
            ),
        },
        {
            key: 'created',
            header: 'Created',
            render: (p) => formatDate(p.created_at),
            className: 'text-text-2 text-[12px] font-mono',
        },
    ];

    return (
        <>
            <DataTable<Proposal>
                columns={columns}
                rows={list.items}
                loading={list.loading}
                totalCount={list.totalCount}
                hasMore={list.hasMore}
                error={list.error}
                onLoadMore={list.loadMore}
                emptyMessage="No strategies yet"
                getRowKey={(r) => r.id}
                onRowClick={(r) => navigate(`/sales/proposals/${r.id}`)}
            />

            <ProposalFlowModal
                open={modalOpen}
                // The wizard saves at step 3 and autosaves later package
                // changes, so refresh on close too to show the final tier.
                onClose={() => {
                    onCloseModal();
                    list.initLoad(1);
                }}
                onCreated={() => {
                    list.initLoad(1);
                }}
            />
        </>
    );
}
